/**
 * WOM collection / product-card ratings (overall_company API).
 * Loaded globally so it runs even when cards are injected via innerHTML (recommended products).
 */
(function () {
  if (window.womCollectionRatingsInitialized) {
    return;
  }
  window.womCollectionRatingsInitialized = true;

  let womOverallCompanyCache = null;

  function ensureWomFontAwesome() {
    if (
      document.getElementById('wom-fontawesome-stars') ||
      document.querySelector('link[href*="font-awesome"][href*="all.min"]')
    ) {
      return;
    }
    const link = document.createElement('link');
    link.id = 'wom-fontawesome-stars';
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
    document.head.appendChild(link);
  }

  /** Single accent star on product cards (score is shown in text next to it). */
  function buildCardRatingStarHtml() {
    if (typeof window.buildWomCardStarHtml === 'function') {
      return window.buildWomCardStarHtml(15);
    }
    return '<svg viewBox="0 0 576 512" width="15" height="15" style="display:block;flex-shrink:0" aria-hidden="true"><path fill="#000000" d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/></svg>';
  }

  function slugifyCore(raw) {
    if (!raw) return '';
    return raw
      .normalize('NFKC')
      .replace(/[^\w]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
  }

  function titleFirstSegment(title) {
    if (!title) return '';
    const head = title.normalize('NFKC').split(/\s*[–—−‒-]\s*/)[0].trim();
    return slugifyCore(head);
  }

  function handleToSlug(handle) {
    if (!handle) return '';
    return handle
      .toLowerCase()
      .replace(/-/g, '_')
      .replace(/[^\w_]+/g, '')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function expandTheVariants(slug) {
    const out = [];
    if (!slug) return out;
    out.push(slug);
    if (!slug.startsWith('the_')) out.push('the_' + slug);
    else {
      const rest = slug.slice(4);
      if (rest) out.push(rest);
    }
    return out;
  }

  function tailStrippedVariants(slug, maxStrips) {
    const variants = [];
    let s = slug;
    for (let i = 0; i <= maxStrips; i++) {
      variants.push(s);
      if (i === maxStrips) break;
      const li = s.lastIndexOf('_');
      if (li <= 0) break;
      const next = s.slice(0, li);
      if (next.split('_').filter(Boolean).length < 2) break;
      s = next;
    }
    return variants;
  }

  function ratingCandidatesForProduct(title, handle) {
    const seen = new Set();
    const add = (slug) => {
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
    };

    const bases = new Set();
    const t1 = titleFirstSegment(title);
    if (t1) bases.add(t1);
    if (title) bases.add(slugifyCore(title));

    const h = handleToSlug(handle);
    if (h) bases.add(h);

    for (const base of bases) {
      for (const stripped of tailStrippedVariants(base, 3)) {
        for (const v of expandTheVariants(stripped)) {
          add(v);
          if (v && !v.startsWith('boldfit_')) add('boldfit_' + v);
        }
      }
    }

    return [...seen];
  }

  function collectionRatingKeysForProduct(title, handle) {
    const cfg = window.vesselsWOMConfig || window.clientWOMConfig || {};

    if (typeof cfg.getCollectionRatingSlug === 'function') {
      const slug = cfg.getCollectionRatingSlug(handle, title);
      return slug ? [slug] : [];
    }

    if (cfg.collectionRatingsFuzzyMatch === true) {
      return ratingCandidatesForProduct(title, handle);
    }

    const keys = [];
    if (handle) {
      keys.push(handle.toLowerCase());
      keys.push(handleToSlug(handle));
    }
    return keys;
  }

  function pickRatingPayload(ratingsData, title, handle) {
    const cfg = window.vesselsWOMConfig || window.clientWOMConfig || {};

    for (const key of collectionRatingKeysForProduct(title, handle)) {
      const data = ratingsData[key];
      if (!data) continue;
      const rating =
        Number.parseFloat(String(data.overall_rating ?? data.overallRating ?? 0)) || 0;
      const count = Number(data.total_reviews ?? data.totalReviews ?? 0) || 0;
      if (rating > 0 && count > 0) return { data, rating, count };
    }

    if (cfg.applyFixedSlugToAllCollectionCards === true) {
      const fixedSlug = cfg.fixedProductSlug || cfg.defaultProductSlug;
      if (fixedSlug && ratingsData[fixedSlug]) {
        const data = ratingsData[fixedSlug];
        const rating =
          Number.parseFloat(String(data.overall_rating ?? data.overallRating ?? 0)) || 0;
        const count = Number(data.total_reviews ?? data.totalReviews ?? 0) || 0;
        if (rating > 0 && count > 0) return { data, rating, count };
      }
    }

    return null;
  }

  function updateCollectionRatings(ratingsData) {
    if (!ratingsData || typeof ratingsData !== 'object') return;

    const ratingElements = document.querySelectorAll('.wom-collection-rating');

    ratingElements.forEach((element) => {
      const productTitle = element.getAttribute('data-product-title') || '';
      const productHandle = element.getAttribute('data-product-handle') || '';

      const picked = pickRatingPayload(ratingsData, productTitle, productHandle);

      if (picked) {
        const numberContainer = element.querySelector('.wom-rating-number');
        const countContainer = element.querySelector('.wom-rating-count');
        const starsFa = element.querySelector('.wom-rating-stars-fa');

        const rating = Math.min(5, Math.max(0, picked.rating));
        if (starsFa) {
          starsFa.innerHTML = buildCardRatingStarHtml();
        }

        if (numberContainer) numberContainer.textContent = rating.toFixed(1);
        if (
          countContainer &&
          !element.classList.contains('wom-collection-rating--media-overlay') &&
          !element.classList.contains('wom-collection-rating--rating-only')
        ) {
          countContainer.textContent = `${picked.count} Reviews`;
        }
        element.style.display = element.classList.contains('wom-collection-rating--media-overlay')
          ? 'inline-flex'
          : 'flex';
      } else {
        element.style.display = 'none';
      }
    });
  }

  function fetchCollectionRatings() {
    ensureWomFontAwesome();
    const cfg = window.vesselsWOMConfig || window.clientWOMConfig || {};
    const apiUrl =
      cfg.overallCompanyApiUrl ||
      `https://api.thewordofmouth.in/api/overall_company/${cfg.companyId || 'boldfit'}`;

    fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        womOverallCompanyCache = data;
        updateCollectionRatings(data);
      })
      .catch((error) => {
        console.error('WOM Collection Ratings: Failed to fetch', error);
      });
  }

  function onWomRefreshCollectionRatings() {
    ensureWomFontAwesome();
    if (womOverallCompanyCache && typeof womOverallCompanyCache === 'object') {
      updateCollectionRatings(womOverallCompanyCache);
    }
    fetchCollectionRatings();
  }

  window.womApplyCachedCollectionRatings = function () {
    if (womOverallCompanyCache && typeof womOverallCompanyCache === 'object') {
      ensureWomFontAwesome();
      updateCollectionRatings(womOverallCompanyCache);
    }
  };

  window.womFetchCollectionRatings = fetchCollectionRatings;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCollectionRatings);
  } else {
    fetchCollectionRatings();
  }

  document.addEventListener('collection-updated', onWomRefreshCollectionRatings);
  document.addEventListener('wom-refresh-collection-ratings', onWomRefreshCollectionRatings);

  window.womNotifyCollectionProductsUpdated = onWomRefreshCollectionRatings;
})();
